/**
 * Cron job CRUD operations for admin interface
 * Handles cron job management using the Dispatcher Pattern
 */

import type { Cron, DbCron } from '@musedrops/shared';
import { fromDbKeys, toDbKeys } from '@musedrops/shared';
import { supabase } from '../services/supabase';

/**
 * Get all cron jobs from the crons table
 */
export async function getAllCrons(): Promise<Cron[]> {
  const { data, error } = await supabase
    .from('crons')
    .select('*')
    .order('name', { ascending: true })
    .returns<DbCron[]>();

  if (error) {
    throw new Error(`Failed to fetch crons: ${error.message}`);
  }

  // Convert from database keys to TypeScript
  return (data ?? []).map(row =>
    fromDbKeys<Cron>(row as unknown as Record<string, unknown>)
  );
}

/**
 * Update a cron job (enabled status or frequency)
 */
export async function updateCron(
  name: string,
  updates: Partial<Pick<Cron, 'enabled' | 'frequencyMinutes'>>
): Promise<Cron> {
  // Convert to database keys
  const dbUpdates = toDbKeys(updates);

  const { data, error } = await supabase
    .from('crons')
    .update(dbUpdates)
    .eq('name', name)
    .select()
    .single<DbCron>();

  if (error) {
    throw new Error(`Failed to update cron ${name}: ${error.message}`);
  }

  if (!data) {
    throw new Error(`Cron ${name} not found`);
  }

  // Convert from database keys to TypeScript
  return fromDbKeys<Cron>(data as unknown as Record<string, unknown>);
}

/**
 * Manually trigger a cron job
 * Directly invokes the Edge Function and updates the crons table
 * (mirrors the cron-dispatcher behavior for manual triggers)
 */
export async function triggerCron(cronName: string): Promise<void> {
  const now = new Date().toISOString();

  try {
    // Update last_run_at BEFORE invocation (same as dispatcher - prevents race conditions)
    const { error: updateError } = await supabase
      .from('crons')
      .update({ last_run_at: now })
      .eq('name', cronName);

    if (updateError) {
      throw new Error(`Failed to update last_run_at: ${updateError.message}`);
    }

    // Invoke the Edge Function (convention: cron.name === Edge Function name)
    const { error: invocationError } = await supabase.functions.invoke(
      cronName,
      {
        body: {},
      }
    );

    if (invocationError) {
      // Update failure status
      const { error: failureUpdateError } = await supabase
        .from('crons')
        .update({
          last_success: false,
          last_error: invocationError.message || 'Invocation failed',
        })
        .eq('name', cronName);

      if (failureUpdateError) {
        // Silently ignore - this is best effort logging
      }

      throw new Error(
        `Failed to invoke ${cronName}: ${invocationError.message}`
      );
    }

    // Update success status
    const { error: successUpdateError } = await supabase
      .from('crons')
      .update({
        last_success: true,
        last_error: null,
      })
      .eq('name', cronName);

    if (successUpdateError) {
      throw new Error(
        `Failed to update success status: ${successUpdateError.message}`
      );
    }
  } catch (error) {
    // Ensure error status is recorded (best effort - don't throw if this fails)
    const { error: recordError } = await supabase
      .from('crons')
      .update({
        last_success: false,
        last_error: error instanceof Error ? error.message : String(error),
      })
      .eq('name', cronName);

    if (recordError) {
      // Silently ignore - this is best effort logging
    }

    throw error;
  }
}
